import React, { useEffect, useMemo, useState } from "react";
import { Button, Tabs } from "antd";
import styles from "./index.module.scss";
import ActivityItem, { PointsActivityItem } from "./components/PointsActivity";
import DesktopPointsActivity from "./components/DesktopPointsActivity";
import { useRequest } from "ahooks";
import { memberRequest, sendMessageToParent } from "../../utils";
import {
  getAllPointsListApi,
  getEarnedListApi,
  getUsedListApi,
} from "../../urls";

import { isMobile } from "react-device-detect";

const PointsActivities = React.forwardRef<
  HTMLDivElement,
  { customerId: number }
>(({ customerId }, ref) => {
  const [expanded, setExpanded] = useState(true);

  const { run: fetchEarnedList, data: earnedListData } = useRequest(
    async ({ memberId }: { memberId: number }) => {
      const params = {
        customer_id: memberId,
        current_page: 1,
        page_size: 10000,
      };

      return memberRequest({
        url: getEarnedListApi,
        method: "GET",
        params,
      });
    }
  );

  const { run: fetchUsedList, data: usedListData } = useRequest(
    async ({ memberId }: { memberId: number }) => {
      const params = {
        customer_id: memberId,
        current_page: 1,
        page_size: 10000,
      };

      return memberRequest({
        url: getUsedListApi,
        method: "GET",
        params,
      });
    }
  );

  const { run: fetchAllPointsList, data: allPointsListData } = useRequest(
    async ({ memberId }: { memberId: number }) => {
      const params = {
        customer_id: memberId,
        current_page: 1,
        page_size: 10000,
      };

      return memberRequest({
        url: getAllPointsListApi,
        method: "GET",
        params,
      });
    }
  );

  const earnedList = useMemo(() => {
    return earnedListData?.data?.data?.total_list || [];
  }, [earnedListData]);

  const usedList = useMemo(() => {
    return usedListData?.data?.data?.total_list || [];
  }, [usedListData]);

  const allPointsList = useMemo(() => {
    return allPointsListData?.data?.data?.total_list || [];
  }, [allPointsListData]);

  const activeTips = useMemo(() => {
    return (
      <div className={styles.noticeText}>
        *Points will be available when your order is delivered.
      </div>
    );
  }, []);

  const Empty = () => {
    return (
      <div className={styles.empty}>
        <p>New to Rewards?</p>
        <p>Complete your purchase to activate points!</p>
      </div>
    );
  };

  // 标签页内容 - 桌面版
  const desktopItems = [
    {
      key: "earned",
      label: <div data-element-id="activities-earned">Earned</div>,
      children: (
        <DesktopPointsActivity
          activities={earnedList}
          filter={(item: PointsActivityItem) => item.change_amount > 0}
          emptyComponent={<Empty />}
        />
      ),
    },
    {
      key: "redeemed",
      label: <div data-element-id="activities-redeemed">Redeemed</div>,
      children: (
        <DesktopPointsActivity
          activities={usedList}
          filter={(item: PointsActivityItem) => item.change_amount < 0}
          emptyComponent={<Empty />}
        />
      ),
    },
    {
      key: "all",
      label: <div data-element-id="activities-all">All</div>,
      children: (
        <DesktopPointsActivity
          activities={allPointsList}
          emptyComponent={<Empty />}
        />
      ),
    },
  ];

  // 保留原有的移动端标签页内容
  const mobileItems = [
    {
      key: "earned",
      label: <div data-element-id="activities-earned">Earned</div>,
      children: (
        <>
          {earnedList.length > 0 ? activeTips : null}
          <div className={styles.activitiesList}>
            {earnedList.length > 0 ? (
              earnedList
                .filter((item: PointsActivityItem) => item.change_amount > 0)
                .map((activity: PointsActivityItem, idx: number) => (
                  <ActivityItem key={idx} activity={activity} />
                ))
            ) : (
              <Empty />
            )}
          </div>
        </>
      ),
    },
    {
      key: "redeemed",
      label: <div data-element-id="activities-redeemed">Redeemed</div>,
      children: (
        <>
          {usedList.length > 0 ? activeTips : null}
          <div className={styles.activitiesList}>
            {usedList.length > 0 ? (
              usedList
                .filter((item: PointsActivityItem) => item.change_amount < 0)
                .map((activity: PointsActivityItem, idx: number) => (
                  <ActivityItem key={idx} activity={activity} />
                ))
            ) : (
              <Empty />
            )}
          </div>
        </>
      ),
    },
    {
      key: "all",
      label: <div data-element-id="activities-all">All</div>,
      children: (
        <>
          {allPointsList.length > 0 ? activeTips : null}
          <div className={styles.activitiesList}>
            {allPointsList.length > 0 ? (
              allPointsList.map((activity: PointsActivityItem, idx: number) => (
                <ActivityItem key={idx} activity={activity} />
              ))
            ) : (
              <Empty />
            )}
          </div>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchEarnedList({ memberId: customerId });
    fetchUsedList({ memberId: customerId });
    fetchAllPointsList({ memberId: customerId });
  }, [fetchAllPointsList, fetchEarnedList, fetchUsedList, customerId]);

  return (
    <div
      ref={ref}
      className={`${styles.pointsActivities} ${
        !isMobile ? styles.desktop : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.title}>Points Activities</div>
        {isMobile && (
          <Button
            type="text"
            className={styles.toggleButton}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "-" : "+"}
          </Button>
        )}
      </div>

      {(expanded || !isMobile) && (
        <div className={styles.content}>
          <Tabs
            defaultActiveKey="earned"
            items={!isMobile ? desktopItems : mobileItems}
            className={styles.tabs}
            onChange={(key) => {
              sendMessageToParent({
                type: "send-datac",
                data: { elementId: `tabs-activities-${key}` },
              });
            }}
          />
        </div>
      )}
    </div>
  );
});

export default PointsActivities;
